import { auth } from '../firebase/firebase';
import { getStructuresForPortals as getStructuresForPortalsFallback } from '../firebase/firestore';

/**
 * Safely extract UID from Firebase ID token (does NOT validate signature)
 * Used only for debugging auth issues
 */
function extractUidFromIdToken(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));
    return payload.uid || payload.sub || null;
  } catch {
    return null;
  }
}

/**
 * Log auth request safely (only if DEBUG_AUTH env var is set in browser)
 * Logs: presence of headers, token preview (first 10 chars), extracted UID, denial reason
 */
function logAuthDebug(context: {
  portalName: string;
  token: string;
  hasAuthHeader: boolean;
  status?: number;
  denialReason?: string;
}): void {
  if (typeof window === 'undefined' || !localStorage.getItem('DEBUG_AUTH')) {
    return;
  }
  
  const uid = extractUidFromIdToken(context.token);
  const tokenPreview = context.token.substring(0, 10);
  
  console.debug(`[PortalStructures] Auth request to "${context.portalName}":`, {
    hasAuthorizationHeader: context.hasAuthHeader,
    tokenPreview: `${tokenPreview}...`,
    uidFromToken: uid || '(could not extract)',
    status: context.status,
    denialReason: context.denialReason || undefined
  });
}

function shouldLogPortalStructuresDebug(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return localStorage.getItem('DEBUG_PORTAL_STRUCTURES') === '1' || localStorage.getItem('DEBUG_AUTH') === '1';
  } catch {
    return false;
  }
}

function portalStructuresDebug(...args: unknown[]): void {
  if (shouldLogPortalStructuresDebug()) {
    console.debug(...args);
  }
}

function shouldBypassPortalBackend(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    if (localStorage.getItem('FORCE_PORTAL_NEXTAPI') === '1') {
      return false;
    }
  } catch {
    // ignore storage issues
  }

  return window.location.hostname === 'localhost' && window.location.port === '3003';
}

function isPortalBackendUnavailable(error: any): boolean {
  const code = String(error?.code || '').toLowerCase();
  const status = Number(error?.status || 0);
  return (
    code === 'backend-unavailable' ||
    code === 'backend-returns-html' ||
    code === 'json-parse-error' ||
    code === 'portal-structures-fetch-failed' ||
    status === 404 ||
    status >= 500
  );
}

function normalizePortalValue(value: unknown): string {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/travelin/g, '')
    .trim();
}

function resolveStructurePortalName(structure: any): string | null {
  return (
    structure?._portalInfo?.portalName ||
    structure?.structure?.portal_administrators?.portal_name ||
    structure?.portal ||
    structure?.portalName ||
    structure?.portal_name ||
    null
  );
}

function enrichPortalStructure(structure: any): any {
  const generatedFilename = generateFilenameFromStructure(structure);
  return {
    ...structure,
    _meta: {
      filename: generatedFilename,
      slug: generatedFilename,
      name: extractStructureName(structure),
      type: extractStructureType(structure),
      compliance: calculateCompliance(structure),
      locality: extractLocality(structure),
      accessibility: extractAccessibilityScoresFromStructure(structure)
    }
  };
}

function assignStructuresToPortalCache(cache: PortalStructureCache, structures: any[]): void {
  const grouped: Record<string, any[]> = {};

  for (const structure of structures) {
    const structurePortalName = resolveStructurePortalName(structure);
    if (!structurePortalName) {
      continue;
    }

    if (!grouped[structurePortalName]) {
      grouped[structurePortalName] = [];
    }

    grouped[structurePortalName].push(structure);
  }

  for (const [portalNameKey, portalStructures] of Object.entries(grouped)) {
    const keys = Object.keys(cache).filter((key) => key !== '_diagnostics');
    const match = keys.find((key) => normalizePortalValue(key) === normalizePortalValue(portalNameKey));
    const bucketKey = match || portalNameKey;
    const enrichedStructures = portalStructures.map(enrichPortalStructure);
    cache[bucketKey] = dedupeByCanonicalId(enrichedStructures, { enableSemanticDedupe: true });
  }
}

async function fetchStructuresByPortalsFallback(userPortals: string[]): Promise<PortalStructureCache> {
  const cache: PortalStructureCache = {};

  for (const portal of userPortals) {
    cache[portal] = [];
  }

  const fallbackStructures = await getStructuresForPortalsFallback(userPortals);
  assignStructuresToPortalCache(cache, Array.isArray(fallbackStructures) ? fallbackStructures : []);
  (cache as any)._diagnostics = {
    source: 'firestore-fallback',
    reason: 'portal-backend-unavailable'
  };

  return cache;
}

/**
 * Generate filename from structure name and type to match static JSON files
 */
function generateFilenameFromStructure(structure: any): string {
  const name = extractStructureName(structure);
  const type = extractStructureType(structure);
  
  if (!name || name === 'Unknown Structure') {
    return structure.id || '';
  }
  
  // Normalize name: lowercase, replace spaces with dashes, remove special chars
  const normalizedName = name
    .toLowerCase()
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    .replace(/[&]/g, '')
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/-+/g, '-') // Replace multiple dashes with single dash
    .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
  
  // Normalize type similarly
  const normalizedType = type
    .toLowerCase()
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    .replace(/[:]/g, '-')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  return `${normalizedName}-${normalizedType}.json`;
}

export interface PortalStructureCache {
  [portalName: string]: any[]; // Changed from DetailedStructure[] to any[] since we're using Firestore data
}

export function getCanonicalStructureId(item: any): string | null {
  const candidate =
    item?.id_backend ??
    item?.backend_id ??
    item?.id ??
    item?._id ??
    item?.structure?.id ??
    item?.structureId ??
    item?.slug ??
    null;

  if (candidate === null || candidate === undefined) return null;
  const normalized = String(candidate).trim();
  return normalized ? normalized : null;
}

function hasNumericValues(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  return Object.values(value as Record<string, unknown>).some(
    (entry) => typeof entry === 'number' && !Number.isNaN(entry)
  );
}

export function hasScores(item: any): boolean {
  const scorePercent = item?.score_percent ?? item?.structure?.score_percent;
  if (typeof scorePercent === 'number' && !Number.isNaN(scorePercent)) return true;

  const verticalScores = item?.vertical_scores_percent ?? item?.structure?.vertical_scores_percent;
  if (hasNumericValues(verticalScores)) return true;

  const privateScores = item?.private_scores ?? item?.structure?.private_scores;
  if (hasNumericValues(privateScores)) return true;

  const metaAccessibility = item?._meta?.accessibility;
  if (hasNumericValues(metaAccessibility)) return true;

  return false;
}

function hasNonUnknownName(candidate: any): boolean {
  const rawName =
    candidate?._meta?.name ??
    candidate?.name ??
    candidate?.structure?.name ??
    candidate?.structure?.accommodation_data?.accommodation_name ??
    '';
  const normalized = String(rawName || '').trim();
  if (!normalized) return false;
  return !normalized.toLowerCase().startsWith('unknown');
}

function hasLocation(candidate: any): boolean {
  const locality =
    candidate?._meta?.locality ??
    candidate?.locality ??
    candidate?.structure?.locality ??
    candidate?.address ??
    candidate?.structure?.address ??
    '';

  if (!locality) return false;
  if (typeof locality === 'string') return locality.trim().length > 0;
  if (typeof locality === 'object') {
    const city = (locality as any).city || (locality as any).province;
    return typeof city === 'string' ? city.trim().length > 0 : false;
  }
  return false;
}

function countDefinedKeys(candidate: any): number {
  if (!candidate || typeof candidate !== 'object') return 0;
  return Object.values(candidate).filter((value) => value !== null && value !== undefined).length;
}

export function pickBetterStructure(a: any, b: any): any {
  const aHasScores = hasScores(a);
  const bHasScores = hasScores(b);
  if (aHasScores !== bHasScores) return aHasScores ? a : b;

  const aHasName = hasNonUnknownName(a);
  const bHasName = hasNonUnknownName(b);
  if (aHasName !== bHasName) return aHasName ? a : b;

  const aHasLocation = hasLocation(a);
  const bHasLocation = hasLocation(b);
  if (aHasLocation !== bHasLocation) return aHasLocation ? a : b;

  const aDefinedKeys = countDefinedKeys(a);
  const bDefinedKeys = countDefinedKeys(b);
  if (aDefinedKeys !== bDefinedKeys) return aDefinedKeys > bDefinedKeys ? a : b;

  return a;
}

function getFallbackName(item: any): string {
  const name = item?._meta?.name ?? item?.name ?? item?.structure?.name;
  return name ? String(name).trim() : '';
}

function getFallbackSlug(item: any): string {
  const slug = item?.slug ?? item?._meta?.slug ?? item?._filename;
  return slug ? String(slug).trim() : '';
}

function getFallbackCity(item: any): string {
  const locality = item?.locality ?? item?._meta?.locality ?? item?.structure?.locality ?? item?.address;
  if (!locality) return '';
  if (typeof locality === 'string') return locality.trim();
  if (typeof locality === 'object') {
    const city = (locality as any).city || (locality as any).province || '';
    return String(city || '').trim();
  }
  return '';
}

function dedupeRawByCanonicalId(items: any[]): any[] {
  const idMap = new Map<string, any>();

  for (const item of items) {
    const canonicalId = getCanonicalStructureId(item);
    const fallbackKey = canonicalId ? null : `name::${getFallbackName(item)}::${getFallbackSlug(item)}::${getFallbackCity(item)}`;
    const key = canonicalId || fallbackKey;
    if (!key) continue;

    if (!idMap.has(key)) {
      idMap.set(key, item);
      continue;
    }

    const existing = idMap.get(key);
    idMap.set(key, pickBetterStructure(existing, item));
  }

  return Array.from(idMap.values());
}

/**
 * Fetch a single page of portal structures
 */
async function fetchPortalStructuresPage(
  portalName: string,
  token: string,
  limit: number = 50,
  cursor?: string
): Promise<{
  ok: boolean;
  items: any[];
  nextCursor?: string;
  status: number;
  contentType: string;
}> {
  const encodedPortal = encodeURIComponent(portalName);
  let url = `/nextapi/ro/portal/structures?portal=${encodedPortal}&fields=compact&limit=${limit}`;
  if (cursor) {
    url += `&cursor=${encodeURIComponent(cursor)}`;
  }

  // Send token in both Authorization (modern) and x-token (legacy backend) headers
  // This ensures compatibility with backend implementations that expect either header
  const res = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-token': token,  // Fallback for backends that expect this format
    },
  });

  // Log auth details if DEBUG_AUTH is enabled in localStorage
  if (res.status === 403) {
    logAuthDebug({
      portalName,
      token,
      hasAuthHeader: true,
      status: 403,
      denialReason: 'backend-returned-403'
    });
    throw { code: 'permission-denied' };
  }
  if (res.status === 503) {
    throw { code: 'missing-index' };
  }
  if (!res.ok) {
    logAuthDebug({
      portalName,
      token,
      hasAuthHeader: true,
      status: res.status,
      denialReason: `http-error-${res.status}`
    });
    throw {
      code: res.status === 404 ? 'backend-unavailable' : 'portal-structures-fetch-failed',
      status: res.status
    };
  }

  // Guardrail: verify content-type is JSON before parsing
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const rawText = await res.text();
    portalStructuresDebug(`[PortalStructures] Non-JSON backend response for "${portalName}"`, {
      contentType,
      preview: rawText.substring(0, 500)
    });
    throw {
      code: 'backend-returns-html',
      message: `Backend returned ${contentType} instead of application/json`,
      contentType,
      preview: rawText.substring(0, 200)
    };
  }

  const rawText = await res.text();
  let payload = null;
  try {
    payload = rawText ? JSON.parse(rawText) : null;
  } catch (parseError) {
    portalStructuresDebug(`[PortalStructures] JSON parse error for "${portalName}"`, parseError);
    throw { code: 'json-parse-error', message: 'Failed to parse JSON response' };
  }

  const items = Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload)
      ? payload
      : [];

  // Log success if DEBUG_AUTH is enabled
  if (localStorage.getItem('DEBUG_AUTH')) {
    const uid = extractUidFromIdToken(token);
    console.debug(`[PortalStructures] ✅ Auth successful for portal "${portalName}":`, {
      uidFromToken: uid || '(could not extract)',
      itemsCount: items.length,
      status: res.status
    });
  }

  return {
    ok: payload?.ok !== false,
    items,
    nextCursor: payload?.nextCursor,
    status: res.status,
    contentType
  };
}

/**
 * Fetch ALL structures for a portal with pagination
 * Uses cursor-based pagination to load all items
 */
async function fetchAllPortalStructures(
  portalName: string,
  token: string
): Promise<{
  items: any[];
  totalPages: number;
  diagnostics: any;
}> {
  const limit = 50;
  const maxPages = 20; // Safety limit to prevent infinite loops
  let allItems: any[] = [];
  let cursor: string | undefined = undefined;
  let pageCount = 0;

  portalStructuresDebug(`[PortalStructures] Fetching all structures for "${portalName}"`);

  while (true) {
    pageCount++;

    if (pageCount > maxPages) {
      portalStructuresDebug(`[PortalStructures] Exceeded max pages (${maxPages}) for "${portalName}"`);
      throw {
        code: 'pagination-limit-exceeded',
        message: `Exceeded maximum page limit of ${maxPages} for portal ${portalName}`,
        itemsLoaded: allItems.length
      };
    }

    portalStructuresDebug(`[PortalStructures] Fetching page ${pageCount} for "${portalName}"${cursor ? ` (cursor: ${cursor.substring(0, 20)}...)` : ''}`);

    const page = await fetchPortalStructuresPage(portalName, token, limit, cursor);

    portalStructuresDebug(`[PortalStructures] Page ${pageCount}: ${page.items.length} items`);

    allItems = dedupeRawByCanonicalId(allItems.concat(page.items));

    // Stop if no more items or no next cursor
    if (page.items.length === 0 || !page.nextCursor) {
      break;
    }

    cursor = page.nextCursor;
  }

  portalStructuresDebug(`[PortalStructures] Loaded ${allItems.length} total structures for "${portalName}" in ${pageCount} pages`);

  return {
    items: allItems,
    totalPages: pageCount,
    diagnostics: {
      portalName,
      totalItems: allItems.length,
      totalPages: pageCount
    }
  };
}

/**
 * Fetches all structures and filters them by portal access using Firestore
 * Returns a cached collection organized by portal name
 * Source: users/{uid}.portals array from Firestore profile
 */
export async function fetchStructuresByPortals(userPortals: string[]): Promise<PortalStructureCache> {
  const cache: PortalStructureCache = {};

  // Initialize cache for each user portal
  for (const portal of userPortals) {
    cache[portal] = [];
  }

  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw { code: 'unauthenticated' };
    }

    // Make individual requests for each portal to ensure proper server-side filtering
    // Uses same-origin URL (nginx proxies /nextapi/* to backoffice)
    for (const portalName of userPortals) {
      if (shouldBypassPortalBackend()) {
        const fallbackCache = await fetchStructuresByPortalsFallback([portalName]);
        cache[portalName] = Array.isArray(fallbackCache[portalName]) ? fallbackCache[portalName] : [];
        (cache as any)._diagnostics = (cache as any)._diagnostics || {};
        (cache as any)._diagnostics[portalName] = {
          source: 'firestore-fallback',
          reason: 'static-local-preview',
          portalRequested: portalName,
        };
        continue;
      }

      let structures: any[] = [];
      let diagnostics: any = null;

      try {
        const result = await fetchAllPortalStructures(portalName, token);
        structures = result.items;
        diagnostics = result.diagnostics;
      } catch (error: any) {
        if (!isPortalBackendUnavailable(error)) {
          throw error;
        }

        const fallbackCache = await fetchStructuresByPortalsFallback([portalName]);
        cache[portalName] = Array.isArray(fallbackCache[portalName]) ? fallbackCache[portalName] : [];
        (cache as any)._diagnostics = (cache as any)._diagnostics || {};
        (cache as any)._diagnostics[portalName] = {
          source: 'firestore-fallback',
          reason: String(error?.code || 'portal-backend-unavailable'),
          portalRequested: portalName,
        };
        continue;
      }

      // Store diagnostic info for debugging
      (cache as any)._diagnostics = (cache as any)._diagnostics || {};
      (cache as any)._diagnostics[portalName] = {
        ...diagnostics,
        portalRequested: portalName
      };
      assignStructuresToPortalCache(cache, structures);
    }
  } catch (error: any) {
    if (error?.code === 'permission-denied' || error?.code === 'missing-index' || error?.code === 'unauthenticated') {
      throw error;
    }
    // Debug log removed to keep console clean
  }

  return cache;
}

/**
 * Extract structure name from Firestore structure data
 */
// Helper functions to extract data from real Firestore structures - EXPORTED
export function extractStructureName(structure: any): string {
  // Debug: Add debugging to see why name extraction is failing
  // Debug log removed to keep console clean

  // Try structure.structure.accommodation_data.accommodation_name (nested in structure)
  if (structure.structure?.accommodation_data?.accommodation_name) {
    return structure.structure.accommodation_data.accommodation_name;
  }

  // Look in modules for structure name, prioritizing field "6.1"
  if (Array.isArray(structure.modules) && structure.modules.length > 0) {
    const firstModule = structure.modules[0];
    if (firstModule?.content) {
      const content = firstModule.content;
      
      // First try the specific field "6.1"
      if (content['6.1'] && typeof content['6.1'] === 'string') {
        return content['6.1'];
      }
      
      // Try other field names as fallback
      const nameFields = [
        'NOME STRUTTURA',
        'Nome struttura', 
        'nome struttura',
        'Denominazione',
        'denominazione'
      ];
      
      for (const field of nameFields) {
        if (content[field] && typeof content[field] === 'string') {
          return content[field];
        }
      }
    }
  }
  
  // Try various possible locations for structure name as final fallback
  if (structure.accommodation_name) return structure.accommodation_name;
  if (structure.name) return structure.name;
  
  return structure.id || 'Unknown Structure';
}

/**
 * Extract structure type from Firestore structure data
 */
export function extractStructureType(structure: any): string {
  // Try structure.structure.type first (this is where we found it)
  if (structure.structure?.type) return structure.structure.type;
  
  // Try structure.accommodation_data.type 
  if (structure.accommodation_data?.type) return structure.accommodation_data.type;
  
  // Try top-level type
  if (structure.type) return structure.type;
  
  // Look in modules for type
  if (Array.isArray(structure.modules) && structure.modules.length > 0) {
    const firstModule = structure.modules[0];
    if (firstModule?.content) {
      const content = firstModule.content;
      
      const typeFields = ['Tipo struttura', 'tipo struttura', 'Type', 'type'];
      for (const field of typeFields) {
        if (content[field] && typeof content[field] === 'string') {
          return content[field];
        }
      }
    }
  }
  
  return 'Unknown';
}

/**
 * Extract locality from Firestore structure data
 */
export function extractLocality(structure: any): string {
  // Try various address fields
  if (structure.address) {
    if (typeof structure.address === 'string') return structure.address;
    if (structure.address.city) return structure.address.city;
    if (structure.address.province) return structure.address.province;
  }
  
  // Look in modules for address
  if (Array.isArray(structure.modules) && structure.modules.length > 0) {
    const firstModule = structure.modules[0];
    if (firstModule?.content) {
      const content = firstModule.content;
      
      const addressFields = ['Indirizzo', 'indirizzo', 'Address', 'address', 'Città', 'città'];
      for (const field of addressFields) {
        if (content[field] && typeof content[field] === 'string') {
          return content[field];
        }
      }
    }
  }
  
  return '';
}

/**
 * Calculate compliance score from structure data
 */
export function calculateCompliance(structure: any): number {
  // If structure has explicit compliance score
  if (structure.compliance && typeof structure.compliance === 'number') {
    return Math.round(structure.compliance);
  }
  
  // Calculate from accessibility scores
  const scores = calculateAccessibilityScores(structure);
  const average = (scores.motoria + scores.visiva + scores.uditiva + scores.cognitiva + scores.alimentare) / 5;
  return Math.round(average);
}

/**
 * Calculate accessibility scores from Firestore structure data
 */
function calculateAccessibilityScores(structure: any): {
  motoria: number;
  visiva: number;
  uditiva: number;
  cognitiva: number;
  alimentare: number;
} {
  // Default scores
  const defaultScores = {
    motoria: 0,
    visiva: 0,
    uditiva: 0,
    cognitiva: 0,
    alimentare: 0
  };

  // Try to find scores in structure data
  if (structure.scores) {
    return {
      motoria: Math.round((structure.scores.motoria || structure.scores.motoria || 0) * 100),
      visiva: Math.round((structure.scores.sensoriale || structure.scores.visiva || 0) * 100),
      uditiva: Math.round((structure.scores.uditiva || structure.scores.uditiva || 0) * 100),
      cognitiva: Math.round((structure.scores.cognitiva || structure.scores.cognitiva || 0) * 100),
      alimentare: Math.round((structure.scores.alimentare || structure.scores.alimentare || 0) * 100)
    };
  }

  // Look for scores in modules or other fields
  // This could be extended based on actual Firestore structure format
  
  return defaultScores;
}

/**
 * Get structures for a specific portal from the cache
 */
export function getStructuresForPortal(cache: PortalStructureCache, portalName: string): any[] {
  // Normalize portal names for robust matching
  const normalize = (s: string) => String(s || '')
    .trim()
    .toLowerCase()
    // remove diacritics
    .normalize('NFD').replace(/\p{Diacritic}/gu, '')
    // collapse non-alphanumerics to single space
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const normalizedPortalName = normalize(portalName);
  const availablePortals = Object.keys(cache);
  const normalizedCache: Record<string, any[]> = {};
  for (const key of availablePortals) {
    normalizedCache[normalize(key)] = cache[key];
  }
  // Debug log removed to keep console clean
  let result = normalizedCache[normalizedPortalName] || [];
  if (result.length === 0) {
    // Print sample of cache keys and portalName for debugging
  // Debug log removed to keep console clean
    // Print fuzzy matches for diagnostics
    const fuzzyMatches = availablePortals.filter(k => normalize(k).includes(normalizedPortalName) || normalizedPortalName.includes(normalize(k)));
    if (fuzzyMatches.length) {
  // Debug log removed to keep console clean
      const first = normalize(fuzzyMatches[0]);
      if (normalizedCache[first]) {
  // Debug log removed to keep console clean
        result = normalizedCache[first];
      }
    }
  }
  // Debug log removed to keep console clean
  return result;
}

/**
 * Check if a user has portal manager or admin access
 */
export function hasPortalAccess(user: any, portals: string[]): boolean {
  if (!user) return false;
  // If the user has any portals assigned, allow access (aligns with navigation guard)
  if (Array.isArray(portals) && portals.length > 0) return true;
  
  // Check direct role property (singular)
  if (user.role === 'PORTAL_MANAGER') {
    return true;
  }
  
  // Check if user has PORTAL_MANAGER role in roles array
  const roles = user?.['https://travelin.com/roles'] || user?.roles || [];
  if (Array.isArray(roles) && roles.includes('PORTAL_MANAGER')) {
    return true;
  }
  
  // Check if user has ADMIN role and has portals assigned
  if ((user.role === 'ADMIN' || (Array.isArray(roles) && roles.includes('ADMIN'))) && portals.length > 0) {
    return true;
  }
  
  return false;
}

/**
 * Calculate aggregated KPIs from cached structures for a portal
 */
export function calculatePortalKPIs(structures: any[]) {
  if (!structures.length) {
    return {
      motoria: 0,
      sensoriale: 0,
      visiva: 0,
      uditiva: 0,
      cognitiva: 0,
      alimentare: 0,
      totalStructures: 0
    };
  }

  const totals = structures.reduce((acc, structure) => {
    // Extract accessibility scores from the actual structure data
    const scores = extractAccessibilityScoresFromStructure(structure);
    acc.motoria += scores.motoria || 0;
    acc.sensoriale += scores.sensoriale || 0;
    acc.visiva += scores.visiva || 0;
    acc.uditiva += scores.uditiva || 0;
    acc.cognitiva += scores.cognitiva || 0;
    acc.alimentare += scores.alimentare || 0;
    return acc;
  }, { motoria: 0, sensoriale: 0, visiva: 0, uditiva: 0, cognitiva: 0, alimentare: 0 });

  const count = structures.length;
  
  // Debug log removed to keep console clean
  
  return {
    motoria: Math.round(totals.motoria / count),
    sensoriale: Math.round(totals.sensoriale / count),
    visiva: Math.round(totals.visiva / count),
    uditiva: Math.round(totals.uditiva / count),
    cognitiva: Math.round(totals.cognitiva / count),
    alimentare: Math.round(totals.alimentare / count),
    totalStructures: count
  };
}

/**
 * Deduplicate structures by ID, keeping the one with the best scores.
 * When duplicate IDs are found, prefer the structure with:
 * 1. private_scores with at least one value > 0
 * 2. score_percent defined
 * 3. Higher accessibility scores
 */
export function dedupeById(items: any[]): any[] {
  const idMap = new Map<string, any>();
  const allIds = [];
  
  for (const item of items) {
    const id = item.id || item._id || item.structure?.id;
    
    if (!id) continue;
    
    allIds.push(id);
    
    if (!idMap.has(id)) {
      // First occurrence
      idMap.set(id, item);
    } else {
      // Duplicate found - keep the "better" one
      const existing = idMap.get(id);
      const isBetter = (candidate: any): boolean => {
        // Check if has real scores
        const hasPrivateScores = () => {
          const scores = candidate?.private_scores || candidate?.structure?.private_scores;
          if (scores && typeof scores === 'object') {
            const values = Object.values(scores).filter((v: any) => typeof v === 'number' && !Number.isNaN(v));
            return values.some((v: any) => v > 0);
          }
          return false;
        };
        
        const hasScorePercent = () => {
          const percent = candidate?.score_percent ?? candidate?.structure?.score_percent;
          return typeof percent === 'number' && !Number.isNaN(percent) && percent > 0;
        };
        
        const getTotalAccessibilityScore = (c: any): number => {
          const accessibility = c._meta?.accessibility || c.vertical_scores_percent || c.structure?.vertical_scores_percent || {};
          if (typeof accessibility === 'object') {
            const values = Object.values(accessibility).filter(
              (v): v is number => typeof v === 'number' && !Number.isNaN(v)
            );
            const sum = values.reduce((acc, v) => acc + v, 0);
            return Number(sum) / Math.max(values.length, 1);
          }
          return 0;
        };
        
        const candidateHasScores = hasPrivateScores() || hasScorePercent();
        const existingHasScores = (existing?.private_scores || existing?.structure?.private_scores) 
          || (existing?.score_percent ?? existing?.structure?.score_percent);
        
        // If candidate has real scores and existing doesn't, candidate wins
        if (candidateHasScores && !existingHasScores) return true;
        
        // If both have scores, keep the one with higher total accessibility
        if (candidateHasScores && existingHasScores) {
          const candScore = getTotalAccessibilityScore(candidate);
          const existScore = getTotalAccessibilityScore(existing);
          return candScore > existScore;
        }
        
        // Otherwise keep existing
        return false;
      };
      
      if (isBetter(item)) {
        idMap.set(id, item);
      }
    }
  }
  
  // Log diagnostics
  const allIdsCount = allIds.length;
  const uniqueIdsCount = idMap.size;
  const duplicateCount = allIdsCount - uniqueIdsCount;
  
  // Find and log duplicate IDs
  const idFrequency = new Map<string, number>();
  for (const id of allIds) {
    idFrequency.set(id, (idFrequency.get(id) || 0) + 1);
  }
  
  const duplicateIds = Array.from(idFrequency.entries())
    .filter(([_, count]) => count > 1)
    .map(([id, count]) => {
      const instances = items.filter(item => (item.id || item._id || item.structure?.id) === id);
      const hasScoresMap = instances.map((inst: any) => ({
        hasPrivateScores: !!(inst.private_scores || inst.structure?.private_scores),
        hasScorePercent: !!(inst.score_percent ?? inst.structure?.score_percent),
        scores: inst._meta?.accessibility || {}
      }));
      return { id, count, instances: hasScoresMap };
    });
  
  if (duplicateCount > 0) {
    console.warn(`⚠️ [DEDUP] Portal structures - Removed ${duplicateCount} duplicates (${allIdsCount} raw → ${uniqueIdsCount} unique)`);
    if (duplicateIds.length > 0) {
      console.warn(`⚠️ [DEDUP] Duplicate ID examples:`, duplicateIds.slice(0, 5));
    }
  }
  
  return Array.from(idMap.values());
}

function normalizeScorePercent(value: unknown): number | null {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  if (value <= 1) return Math.round(value * 100);
  return Math.round(value);
}

export function getTotalScorePercent(item: any): number | null {
  const scorePercent = item?.score_percent ?? item?.structure?.score_percent;
  const normalizedScorePercent = normalizeScorePercent(scorePercent);
  if (normalizedScorePercent !== null) return normalizedScorePercent;

  const privateScores = item?.private_scores || item?.structure?.private_scores;
  if (privateScores && typeof privateScores === 'object') {
    const values = Object.values(privateScores)
      .filter((score): score is number => typeof score === 'number' && !Number.isNaN(score));
    if (values.length) {
      const average = values.reduce((sum, score) => sum + score, 0) / values.length;
      return normalizeScorePercent(average);
    }
  }

  return null;
}

export function getVerticalPercent(item: any, vertical: string): number | null {
  const verticalScores = item?.vertical_scores_percent || item?.structure?.vertical_scores_percent;
  const privateScores = item?.private_scores || item?.structure?.private_scores;
  const fallbackVertical = vertical === 'visiva' || vertical === 'uditiva' ? 'sensoriale' : vertical;

  const verticalValue =
    verticalScores?.[vertical] ??
    verticalScores?.[fallbackVertical] ??
    privateScores?.[vertical] ??
    privateScores?.[fallbackVertical];

  return normalizeScorePercent(verticalValue);
}

/**
 * Extract accessibility scores from real structure data
 */
export function extractAccessibilityScoresFromStructure(structure: any): any {
  const defaultScores = { motoria: null, sensoriale: null, visiva: null, uditiva: null, cognitiva: null, alimentare: null };
  
  // Debug: Log structure data to understand what's available (first few structures only)
  // Debug log removed to keep console clean
  
  const verticalScores = structure.vertical_scores_percent || structure.structure?.vertical_scores_percent;
  const privateScores = structure.private_scores || structure.structure?.private_scores;

  if (verticalScores || privateScores) {
    return {
      motoria: getVerticalPercent(structure, 'motoria'),
      sensoriale: getVerticalPercent(structure, 'sensoriale'),
      visiva: getVerticalPercent(structure, 'visiva'),
      uditiva: getVerticalPercent(structure, 'uditiva'),
      cognitiva: getVerticalPercent(structure, 'cognitiva'),
      alimentare: getVerticalPercent(structure, 'alimentare')
    };
  }
  
  // Try other locations for accessibility scores  
  if (structure.accessibility_scores) return { ...defaultScores, ...structure.accessibility_scores };
  if (structure.scores) return { ...defaultScores, ...structure.scores };
  if (structure.accessibility) return { ...defaultScores, ...structure.accessibility };
  
  // Look in accommodation_data
  if (structure.accommodation_data?.accessibility) {
    return { ...defaultScores, ...structure.accommodation_data.accessibility };
  }
  
  // Look in meta if available
  if (structure._meta?.accessibility) {
    return { ...defaultScores, ...structure._meta.accessibility };
  }
  
  // Calculate estimated scores from modules if available
  if (Array.isArray(structure.modules) && structure.modules.length > 0) {
    const scores = { ...defaultScores };
    let foundScores = false;
    
    structure.modules.forEach((module: any) => {
      if (module.content) {
        // Look for accessibility-related fields and estimate scores
        Object.keys(module.content).forEach(key => {
          const lowerKey = key.toLowerCase();
          const value = module.content[key];
          
          // Try to parse numeric scores
          if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
            const numValue = typeof value === 'number' ? value : parseFloat(value);
            
            if (lowerKey.includes('mobilità') || lowerKey.includes('motoria') || lowerKey.includes('motoria')) {
              scores.motoria = Math.max(scores.motoria, numValue); foundScores = true;
            }
            if (lowerKey.includes('vista') || lowerKey.includes('visiva') || lowerKey.includes('sensoriale')) {
              const sensorScore = Math.max(scores.visiva, numValue);
              scores.sensoriale = Math.max(scores.sensoriale, numValue);
              scores.visiva = sensorScore;
              scores.uditiva = sensorScore; // Map sensoriale to both
              foundScores = true;
            }
            if (lowerKey.includes('udito') || lowerKey.includes('uditiva')) {
              scores.uditiva = Math.max(scores.uditiva, numValue); foundScores = true;
            }
            if (lowerKey.includes('cognitiv') || lowerKey.includes('cognitiva') || lowerKey.includes('cognitiva')) {
              scores.cognitiva = Math.max(scores.cognitiva, numValue); foundScores = true;
            }
            if (lowerKey.includes('alimentare') || lowerKey.includes('cibo') || lowerKey.includes('alimentare')) {
              scores.alimentare = Math.max(scores.alimentare, numValue); foundScores = true;
            }
          }
          
          // Look for boolean accessibility features and estimate scores
          if (typeof value === 'boolean' || (typeof value === 'string' && (value.toLowerCase() === 'true' || value.toLowerCase() === 'false' || value.toLowerCase() === 'sì' || value.toLowerCase() === 'no'))) {
            const boolValue = value === true || value === 'true' || (typeof value === 'string' && value.toLowerCase() === 'sì');
            const scoreBoost = boolValue ? 20 : 0;
            
            if (lowerKey.includes('rampa') || lowerKey.includes('ascensore') || lowerKey.includes('wheelchair')) {
              scores.motoria = Math.min(100, scores.motoria + scoreBoost); foundScores = true;
            }
            if (lowerKey.includes('braille') || lowerKey.includes('contrasto') || lowerKey.includes('visual')) {
              scores.visiva = Math.min(100, scores.visiva + scoreBoost); foundScores = true;
              scores.sensoriale = Math.min(100, scores.sensoriale + scoreBoost);
            }
            if (lowerKey.includes('audio') || lowerKey.includes('uditiva') || lowerKey.includes('suono')) {
              scores.uditiva = Math.min(100, scores.uditiva + scoreBoost); foundScores = true;
              scores.sensoriale = Math.min(100, scores.sensoriale + scoreBoost);
            }
            if (lowerKey.includes('semplice') || lowerKey.includes('chiaro') || lowerKey.includes('cognitive')) {
              scores.cognitiva = Math.min(100, scores.cognitiva + scoreBoost); foundScores = true;
            }
            if (lowerKey.includes('allergen') || lowerKey.includes('dieta') || lowerKey.includes('alimentare')) {
              scores.alimentare = Math.min(100, scores.alimentare + scoreBoost); foundScores = true;
            }
          }
        });
      }
    });
    
    if (foundScores) return scores;
  }
  
  return defaultScores;
}

export function dedupeByCanonicalId(items: any[], options?: { enableSemanticDedupe?: boolean }): any[] {
  const idMap = new Map<string, any>();
  const allIds: string[] = [];
  const enableSemanticDedupe = options?.enableSemanticDedupe === true;

  const normalizeSemanticKey = (value: string): string => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[.,;:!?'"()\[\]{}<>]/g, '')
      .replace(/\s+/g, ' ');
  };

  for (const item of items) {
    const canonicalId = getCanonicalStructureId(item);
    const fallbackKey = canonicalId ? null : `name::${getFallbackName(item)}::${getFallbackSlug(item)}::${getFallbackCity(item)}`;
    const key = canonicalId || fallbackKey;
    if (!key) continue;

    allIds.push(key);

    if (!idMap.has(key)) {
      idMap.set(key, item);
    } else {
      const existing = idMap.get(key);
      idMap.set(key, pickBetterStructure(existing, item));
    }
  }

  const allIdsCount = allIds.length;
  const uniqueIdsCount = idMap.size;
  const duplicateCount = allIdsCount - uniqueIdsCount;

  if (duplicateCount > 0) {
    console.warn(`⚠️ [DEDUP] Portal structures - Removed ${duplicateCount} duplicates by canonical ID (${allIdsCount} raw → ${uniqueIdsCount} unique)`);
  }

  let dedupedItems = Array.from(idMap.values());

  if (enableSemanticDedupe && duplicateCount > 0) {
    const semanticMap = new Map<string, any>();
    for (const item of dedupedItems) {
      const rawSemantic = getFallbackSlug(item) || getFallbackName(item);
      const semanticKey = rawSemantic ? normalizeSemanticKey(rawSemantic) : '';
      if (!semanticKey) {
        const fallbackKey = `fallback::${getFallbackName(item)}::${getFallbackCity(item)}`;
        semanticMap.set(fallbackKey, item);
        continue;
      }

      if (!semanticMap.has(semanticKey)) {
        semanticMap.set(semanticKey, item);
        continue;
      }

      const existing = semanticMap.get(semanticKey);
      semanticMap.set(semanticKey, pickBetterStructure(existing, item));
    }

    dedupedItems = Array.from(semanticMap.values());
  }

  return dedupedItems;
}